/**
 * GPS over a serial (COM) port — a USB GPS puck or a laptop's built-in GPS module — read straight from
 * Chrome/Edge with the Web Serial API. Nothing to install on the laptop. The deputy picks the port once;
 * after that it reconnects by itself. While it's sending fixes, it's used instead of Windows location.
 */
import { NmeaDecoder, type NmeaFix } from '@civilos/domain';

export type SerialGpsStatus = 'unsupported' | 'off' | 'connecting' | 'nofix' | 'on' | 'error';
export interface SerialGpsState {
  status: SerialGpsStatus;
  baud: number | null;
  satellites: number | null;
  lastFixAt: number | null;
  error: string | null;
  remembered: boolean;
}
export type SerialFix = NmeaFix & { at: number };

const PREF = 'civilhound.serialGps';
const BAUDS = [4800, 9600, 38400, 115200, 19200, 57600];
const nav = navigator as any;
export const serialSupported = typeof navigator !== 'undefined' && 'serial' in navigator;

let state: SerialGpsState = { status: serialSupported ? 'off' : 'unsupported', baud: null, satellites: null, lastFixAt: null, error: null, remembered: !!readPref() };
const stateListeners = new Set<(s: SerialGpsState) => void>();
const fixListeners = new Set<(f: SerialFix) => void>();
let port: any = null;
let reader: any = null;
let stopping = false;

function readPref(): { baud: number; usbVendorId?: number; usbProductId?: number } | null {
  try {
    return JSON.parse(localStorage.getItem(PREF) ?? 'null');
  } catch {
    return null;
  }
}
function writePref(v: unknown) {
  try {
    if (v == null) localStorage.removeItem(PREF);
    else localStorage.setItem(PREF, JSON.stringify(v));
  } catch {
    /* private window: just won't auto-reconnect */
  }
}
function set(p: Partial<SerialGpsState>) {
  state = { ...state, ...p };
  stateListeners.forEach((l) => l(state));
}
export const serialGpsState = () => state;
export function onSerialGpsState(l: (s: SerialGpsState) => void) {
  stateListeners.add(l);
  return () => void stateListeners.delete(l);
}
export function onSerialFix(l: (f: SerialFix) => void) {
  fixListeners.add(l);
  return () => void fixListeners.delete(l);
}
/** A serial fix newer than this is preferred over the browser's location. */
export const SERIAL_FRESH_MS = 4000;
export const serialIsFresh = () => state.lastFixAt != null && Date.now() - state.lastFixAt < SERIAL_FRESH_MS;

async function closePort() {
  try {
    await reader?.cancel();
  } catch {
    /* already closed */
  }
  try {
    reader?.releaseLock();
  } catch {
    /* ignore */
  }
  reader = null;
  try {
    await port?.close();
  } catch {
    /* ignore */
  }
}

/** Open at a baud rate and read; resolves true once real NMEA sentences arrive (within ~2.5 s). */
async function tryBaud(p: any, baud: number): Promise<boolean> {
  try {
    await p.open({ baudRate: baud });
  } catch (e: any) {
    if (/already open/i.test(e?.message ?? '')) {
      await p.close().catch(() => undefined);
      await p.open({ baudRate: baud });
    } else throw e;
  }
  port = p;
  const dec = new NmeaDecoder();
  const text = new TextDecoder();
  reader = p.readable.getReader();
  let resolveOk: (v: boolean) => void = () => undefined;
  const ok = new Promise<boolean>((r) => (resolveOk = r));
  const timer = setTimeout(() => resolveOk(false), 2500);
  (async () => {
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const fixes = dec.push(text.decode(value, { stream: true }));
        if (dec.goodSentences >= 2) resolveOk(true);
        for (const f of fixes) {
          const at = Date.now();
          set({ status: 'on', satellites: f.satellites, lastFixAt: at });
          fixListeners.forEach((l) => l({ ...f, at }));
        }
        if (!fixes.length && dec.goodSentences >= 2 && (state.lastFixAt == null || Date.now() - state.lastFixAt > 5000) && state.status !== 'connecting') {
          set({ status: 'nofix' });
        }
      }
    } catch {
      /* port unplugged or cancelled */
    } finally {
      clearTimeout(timer);
      resolveOk(false);
      if (!stopping && port === p && state.status !== 'connecting') set({ status: 'off', error: 'GPS disconnected' });
    }
  })();
  const good = await ok;
  clearTimeout(timer);
  if (!good) await closePort();
  return good;
}

async function connectPort(p: any, preferBaud?: number) {
  stopping = false;
  set({ status: 'connecting', error: null });
  const bauds = preferBaud ? [preferBaud, ...BAUDS.filter((b) => b !== preferBaud)] : BAUDS;
  try {
    for (const b of bauds) {
      if (await tryBaud(p, b)) {
        const info = p.getInfo?.() ?? {};
        writePref({ baud: b, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId });
        set({ status: state.lastFixAt && Date.now() - state.lastFixAt < 3000 ? 'on' : 'nofix', baud: b, remembered: true });
        return true;
      }
    }
    set({ status: 'error', error: 'No GPS data on that port. Pick the other port, or check that the GPS is on.' });
  } catch (e: any) {
    set({ status: 'error', error: /in use|access denied|failed to open/i.test(e?.message ?? '') ? 'That port is in use by another program (CAD or mapping software). Close it, or use a USB GPS.' : e?.message ?? 'Could not open the port' });
  }
  return false;
}

/** "Connect GPS" button: Chrome shows the list of serial ports; the deputy picks one. */
export async function chooseSerialGps() {
  if (!serialSupported) return;
  let p: any;
  try {
    p = await nav.serial.requestPort();
  } catch {
    return; // closed the picker
  }
  await closePort();
  await connectPort(p, readPref()?.baud);
}

export async function disconnectSerialGps() {
  stopping = true;
  await closePort();
  port = null;
  writePref(null);
  set({ status: serialSupported ? 'off' : 'unsupported', baud: null, satellites: null, lastFixAt: null, error: null, remembered: false });
}

/** On app start (and when the GPS is plugged back in): reconnect to the port picked before. */
let started = false;
export function startSerialGps() {
  if (!serialSupported || started) return;
  started = true;
  const reconnect = async () => {
    const pref = readPref();
    if (!pref || ['connecting', 'on', 'nofix'].includes(state.status)) return;
    const ports: any[] = await nav.serial.getPorts().catch(() => []);
    const match = ports.find((x) => {
      const i = x.getInfo?.() ?? {};
      return pref.usbVendorId == null || (i.usbVendorId === pref.usbVendorId && i.usbProductId === pref.usbProductId);
    });
    if (match) await connectPort(match, pref.baud);
  };
  nav.serial.addEventListener?.('connect', () => void reconnect());
  nav.serial.addEventListener?.('disconnect', (e: any) => {
    if (e?.target === port || e?.port === port) set({ status: 'off', error: 'GPS unplugged' });
  });
  void reconnect();
}
